import * as React from "react";
import {useTheme} from "@mui/material/styles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import {Alert, AppBar, Card, TextField, Typography} from "@mui/material";
import {FormControl, InputLabel, MenuItem, Select} from "@mui/material";
import MDButton from "components/MDButton";
import {POST, GET} from "APIclient";
import dayjs from "dayjs";
import {useState} from "react";
import {DemoContainer} from "@mui/x-date-pickers/internals/demo";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import Snackbar from "@mui/material/Snackbar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useLocation, useNavigate} from "react-router-dom";
import properties from "constants/ApplicationConstants";

export default function AddProject() {
  const location = useLocation();
  const [usersState, setUsersState] = useState(location.state);
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [clients, setClients] = React.useState([]);
  const [isSaveDisabled, setIsSaveDisabled] = React.useState(true);

  const [formData, setFormData] = React.useState({
    projectName: "",
    projectType: "",
    duration: "",
    clientId: "",
    startDate: null,
    completeDate: null
  });
  const [errors, setErrors] = React.useState({
    projectName: "",
    projectType: ""
  });

  React.useEffect(() => {
    const fetchUrl = `admin/project/index`;
    const fetchData = async () => {
      try {
        const response = await GET(fetchUrl);
        const data = response?.data;
        if (data) {
          setFormData({
            projectName: data.projectName || "",
            projectType: data.projectType || "",
            duration: data.duration || "",
            clientId: data.clientId || "",
            startDate: data.startDate ? dayjs(data.startDate) : null,
            completeDate: data.completeDate ? dayjs(data.completeDate) : null
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    const fetchClient = async () => {
      try {
        const clientResponse = await GET("admin/client-list?page=0&size=1000");
        setClients(clientResponse.data?.data?.paginatedData || []);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClient();
    fetchData();
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "projectName":
        return value.trim() ? "" : "Project Name is required";
      case "projectType":
        return value ? "" : "Project Type is required";
      default:
        return "";
    }
  };

  const handleChange = e => {
    const {name, value} = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleDateChange = (date, field) => {
    const validDate = date ? dayjs(date) : null;
    setFormData(prev => ({
      ...prev,
      [field]: validDate?.isValid() ? validDate.format("YYYY-MM-DD") : null
    }));
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    const newErrors = {
      projectName: validateField("projectName", formData.projectName),
      projectType: validateField("projectType", formData.projectType)
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(error => error)) {
      return;
    }

    const url = `admin/project/add`;
    const dt = {
      projectName: formData.projectName,
      projectType: parseInt(formData.projectType) || null,
      clientId: formData.clientId || null,
      duration: parseInt(formData.duration) || null,
      startDate: formData.startDate || null,
      completeDate: formData.completeDate || null
    };

    try {
      const response = await POST(url, dt);
      console.log("response", response);
      const data = response.data;
      if (data.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage("Project Added successfully");
        setOpen(true);

        setTimeout(() => {
          navigate("/projectList");
        }, 1500);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const message = error?.response?.data?.message || "Failed to add project";
      if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Project name already exists.");
      } else if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage("Bad request. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(message);
      }
      setOpen(true);
    }
  };

  React.useEffect(() => {
    setIsSaveDisabled(!(formData.projectName.trim() && formData.projectType));
  }, [formData.projectName, formData.projectType]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox
              pt={2}
              mb={0}
              pb={4}
              px={4}
              sx={{borderRadius: "5px", border: "1px solid #D3D3D3"}}
            >
              <Typography
                sx={{
                  color: "textPrimary",
                  fontFamily: properties.fontFamily,
                  fontSize: "16px",
                  pb: 2,
                  fontWeight: "bold"
                }}
                id="project"
              >
                Add Project
              </Typography>
              <TextField
                sx={{m: 0, pb: 2, width: "30ch"}}
                name="projectName"
                value={formData.projectName || ""}
                type="text"
                label={
                  <span>
                    Project Name <span style={{color: properties.colors.Red}}>*</span>
                  </span>
                }
                variant="standard"
                onChange={e => handleChange(e)}
                error={!!errors.projectName}
                helperText={errors.projectName}
              />

              <FormControl variant="standard" sx={{m: 1, width: "25ch", paddingRight: "10px"}}>
                <InputLabel id="projectType">
                  Project Type <span style={{color: properties.colors.Red}}>*</span>
                </InputLabel>
                <Select
                  value={formData.projectType || ""}
                  name="projectType"
                  label="Project Type"
                  onChange={e => handleChange(e)}
                  IconComponent={ExpandMoreIcon}
                  error={!!errors.projectType}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  <MenuItem value="1">In-House</MenuItem>
                  <MenuItem value="2">Client-Side</MenuItem>
                </Select>
                {errors.projectType && (
                  <MDBox sx={{color: properties.colors.Red, fontSize: "0.75rem", mt: 0.5}}>
                    {errors.projectType}
                  </MDBox>
                )}
              </FormControl>
              <FormControl variant="standard" sx={{m: 1, width: "25ch"}}>
                <InputLabel id="clientId">Client Name</InputLabel>
                <Select
                  value={formData.clientId || ""}
                  name="clientId"
                  label="Client Name"
                  onChange={e => handleChange(e)}
                  IconComponent={ExpandMoreIcon}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  {clients.map(client => (
                    <MenuItem key={client.clientId} value={client.clientId}>
                      {client.clientName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]} sx={{marginTop: "10px"}}>
                  <MDInput
                    sx={{m: 1, width: "30ch"}}
                    type="text"
                    name="duration"
                    value={formData.duration || ""}
                    label="Project Duration"
                    variant="standard"
                    onChange={e => handleChange(e)}
                  />
                  <DatePicker
                    format="DD/MM/YYYY"
                    sx={{m: 1, width: "30ch"}}
                    size="small"
                    label="Project Start Date"
                    name="startDate"
                    value={formData.startDate ? dayjs(formData.startDate) : null}
                    onChange={date => handleDateChange(date, "startDate")}
                  />
                  <DatePicker
                    format="DD/MM/YYYY"
                    sx={{m: 1, width: "30ch"}}
                    size="small"
                    label="Project End Date"
                    name="completeDate"
                    value={formData.completeDate ? dayjs(formData.completeDate) : null}
                    onChange={date => handleDateChange(date, "completeDate")}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </MDBox>

            <MDBox mt={3} mb={2} sx={{display: "flex", justifyContent: "flex-end"}}>
              <MDBox sx={{display: "flex", gap: 1}}>
                <MDButton
                  variant="gradient"
                  color="warning"
                  sx={{width: "15ch", fontFamily: properties.fontFamily}}
                  onClick={() => navigate(-1)}
                >
                  Back
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  sx={{
                    marginRight: "10px",
                    width: "15ch",
                    mr: 2,
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  onClick={handleSubmit}
                  disabled={isSaveDisabled}
                >
                  Save
                </MDButton>
              </MDBox>
            </MDBox>
            <Snackbar
              open={open}
              anchorOrigin={{vertical: "top", horizontal: "right"}}
              autoHideDuration={6000}
              onClose={handleClose}
            >
              <Alert
                autoHideDuration={1200}
                onClose={handleClose}
                severity={snakColor}
                variant="filled"
                sx={{width: "100%"}}
              >
                {snakMessage}
              </Alert>
            </Snackbar>
          </Card>
        </AppBar>
      </MDBox>
    </DashboardLayout>
  );
}
